/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * Wire `RequestType` values this subsystem understands.
 *
 * In this project the server sends the notification type as the MRPC/2
 * `RequestType` header, which WebSocketMrpcv2Enveloper maps onto
 * `IWebSocketMessage.operationName`. That string is therefore the single
 * authoritative dispatch key for the whole push-notification path.
 *
 * FUTURE SCOPE — add one member per newly supported RequestType:
 *   CHANNEL_LIST_UPDATE = "channelListUpdateNotification"
 *   COMMAND = "commandNotification"
 *   DEVICE_STATUS_UPDATE = "deviceStatusUpdateNotification"
 *   NDVR_UPDATE = "ndvrUpdateNotification"
 */
export enum PushNotificationType {
  /**
   * Renamed from `emergencyAlertDisplayNotification` when the server aligned
   * the MRPC/2 dispatch RequestType with the wire body's own `requestType`
   * self-description (see EasAlertMessage in
   * js-domain-lib/lib/client-code/easalert-spec/models/eas-alert-message.ts)
   * — the two are now the same string.
   */
  EMERGENCY_ALERT_DISPLAY = "emergencyAlertMessage"
}

/**
 * Transport that delivered the notification.
 *
 * Carried through to consumers so downstream logic stays transport-blind
 * while telemetry can still attribute the delivery.
 */
export enum PushNotificationSource {
  WEBSOCKET = "websocket"
}

/**
 * EAS `alertType` system codes identifying the alert event type.
 *
 * Authored here rather than imported because the generated spec types
 * `alertType` as a plain string — see
 * js-domain-lib/lib/client-code/easalert-spec/models/eas-alert.ts.
 *
 * Unrecognised and absent values normalise to UNKNOWN and are still
 * delivered: the alert text must be shown regardless of the code.
 */
export enum EamAlertType {
  EAN = "EAN",
  RWT = "RWT",
  UNKNOWN = "unknown"
}

/**
 * Derived audio/video nature of the EAS media.
 *
 * The three content values correspond to the service-provided
 * EasAlertAudioVideoContentEnum, and WebSocketNotificationParser maps them
 * through that enum rather than through string literals — so the mapping
 * is bound to the schema and a new upstream value fails to compile.
 *
 * This stays a separate enum solely because it adds NONE: every EasAlert
 * field is optional, so the parser needs a value meaning "absent or
 * unrecognised" that the service enum does not define.
 */
export enum EamContentAvType {
  AUDIO_ONLY = "audioOnly",
  AUDIO_VIDEO = "audioVideo",
  VIDEO_ONLY = "videoOnly",
  NONE = "none"
}

/**
 * Metadata every parsed notification carries, independent of its type.
 *
 * Every field is non-optional and defaults to an empty string when the
 * corresponding header is absent, so consumers never need null checks.
 */
export interface TPushNotificationMetadata {
  /** MRPC/2 `Origin-RequestId` header — the dedupe key. */
  originRequestId: string;
  /** MRPC/2 `RpcId` header. */
  rpcId: string;
  /** MRPC/2 `BodyId` header. */
  bodyId: string;
  /** Transport that delivered this notification. */
  source: PushNotificationSource;
  /** Local clock at receive time, in epoch milliseconds. */
  receivedTimeMs: number;
}

/**
 * Parsed `emergencyAlertMessage` payload.
 *
 * `notificationType` is the discriminant of the TPushNotification union.
 * All EasAlert fields are optional on the wire and are normalised here to
 * non-optional values so no consumer needs optional handling.
 */
export interface TEamPushNotification extends TPushNotificationMetadata {
  /** Discriminant — always EMERGENCY_ALERT_DISPLAY. */
  notificationType: PushNotificationType.EMERGENCY_ALERT_DISPLAY;
  /** Alert text; "" when absent or empty on the wire. */
  text: string;
  /** Playback URL; "" when absent. */
  mediaUrl: string;
  /** Derived from `audioVideoContent`; NONE when absent or unrecognised. */
  contentAvType: EamContentAvType;
  /** Whether the user may dismiss the alert; false when absent. */
  allowUserExit: boolean;
  /** Alert event code; UNKNOWN when absent or unrecognised. */
  alertType: EamAlertType;
}

/**
 * Discriminated union of every parsed notification.
 *
 * FUTURE SCOPE: widen this union as types are added. Consumers must always
 * switch on `notificationType` rather than assume a single member —
 * PushNotificationRouter enforces exhaustiveness at compile time.
 */
export type TPushNotification = TEamPushNotification;

/**
 * Error codes for the push-notification subsystem.
 */
export enum PushNotificationErrorCode {
  UNSUPPORTED_TYPE = "UNSUPPORTED_TYPE",
  MALFORMED_PAYLOAD = "MALFORMED_PAYLOAD"
}

/**
 * Custom error class for the push-notification subsystem.
 */
export class PushNotificationError extends Error {
  public readonly code: PushNotificationErrorCode;

  constructor(code: PushNotificationErrorCode, message: string) {
    super(message);
    this.name = "PushNotificationError";
    this.code = code;
  }
}
